import { useEffect, useState } from 'react';
import { useMsalAccount } from '@higgins/ui';
import { getRecentJobs, isConnectionFailure } from './api';
import { useOnline } from './hooks';
import { msalInstance } from './msal';
import type { RecentJob } from './types';

/**
 * CRM's Recent jobs list as last fetched, so the home page has it with no
 * signal: one per signed-in account, so someone else signing in on this
 * browser never sees the last person's jobs.
 */
const cacheKey = (accountId: string) => `m2q-recent-jobs:${accountId}`;

function readCache(key: string | null): RecentJob[] {
  if (!key) return [];
  try {
    const cached = JSON.parse(localStorage.getItem(key) ?? 'null') as { jobs?: unknown } | null;
    return Array.isArray(cached?.jobs) ? (cached.jobs as RecentJob[]) : [];
  } catch {
    return [];
  }
}

function writeCache(key: string, jobs: RecentJob[]): void {
  try {
    localStorage.setItem(key, JSON.stringify({ jobs }));
  } catch {
    // Storage full or blocked: the list still shows; it just isn't there next time with no signal.
  }
}

/** The jobs the user opened on any device (#422): fetched whenever there's signal, else the last list fetched. */
export function useRecentJobsFromCrm(): RecentJob[] {
  const online = useOnline();
  const accountId = useMsalAccount(msalInstance)?.homeAccountId;
  const key = accountId ? cacheKey(accountId) : null;
  const [fetched, setFetched] = useState<{ key: string; jobs: RecentJob[] } | null>(null);
  useEffect(() => {
    if (!online || !key) return;
    let current = true;
    getRecentJobs().then(
      (answer) => {
        if (!current) return;
        setFetched({ key, jobs: answer.jobs });
        writeCache(key, answer.jobs);
      },
      (err: unknown) => {
        if (!isConnectionFailure(err)) console.warn('Could not fetch recent jobs', err);
      },
    );
    return () => {
      current = false;
    };
  }, [online, key]);
  // This account's list from CRM once fetched; until then (or with no signal) the last one kept here.
  return fetched?.key === key ? fetched.jobs : readCache(key);
}
